// Firebase Authentication Service
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  onAuthStateChanged,
  type User,
  type UserCredential,
} from 'firebase/auth';
import { doc, getDoc, getDocFromServer, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './config';
import type { UserProfile, UserRole } from '@/types';

// Google Auth provider
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');
// Always show account picker so users can choose which Google account to use
googleProvider.setCustomParameters({ prompt: 'select_account' });

// Super admin email from environment
const SUPER_ADMIN_EMAIL = import.meta.env.VITE_SUPER_ADMIN_EMAIL;

// Determine user role based on email
const determineUserRole = (email: string): UserRole => {
  if (email === SUPER_ADMIN_EMAIL) {
    return 'super_admin';
  }
  return 'owner'; // New users become owners of their first organization
};

// Create or update user profile in Firestore
const createOrUpdateUserProfile = async (user: User): Promise<UserProfile> => {
  const userRef = doc(db, 'users', user.uid);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    // Update last login
    await updateDoc(userRef, {
      lastLoginAt: serverTimestamp(),
      displayName: user.displayName || userSnap.data().displayName,
      photoURL: user.photoURL || userSnap.data().photoURL,
    });
    return { id: userSnap.id, ...userSnap.data() } as UserProfile;
  } else {
    // Create new user profile
    const role = determineUserRole(user.email || '');
    const newProfile = {
      email: user.email || '',
      displayName: user.displayName || user.email?.split('@')[0] || 'User',
      photoURL: user.photoURL || null,
      role,
      organizationIds: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: user.uid,
      lastLoginAt: serverTimestamp(),
    };

    await setDoc(userRef, newProfile);
    return { id: user.uid, ...newProfile } as unknown as UserProfile;
  }
};

export const authService = {
  // Check for redirect result on page load (called by AuthContext)
  // This handles the case when OAuth used redirect instead of popup
  checkRedirectResult: async (): Promise<{ user: User; profile: UserProfile } | null> => {
    try {
      const result: UserCredential | null = await getRedirectResult(auth);
      if (result && result.user) {
        console.log('OAuth redirect result received for:', result.user.email);
        const profile = await createOrUpdateUserProfile(result.user);
        return { user: result.user, profile };
      }
      return null;
    } catch (error) {
      console.error('Error checking redirect result:', error);
      return null;
    }
  },

  // OAuth using popup with fallback to redirect for mobile/blocked popups
  signInWithGoogle: async (): Promise<{ user: User; profile: UserProfile }> => {
    try {
      // Try popup first (works well on desktop)
      const result = await signInWithPopup(auth, googleProvider);
      const profile = await createOrUpdateUserProfile(result.user);
      return { user: result.user, profile };
    } catch (error: unknown) {
      const errorCode = (error as { code?: string })?.code;
      // If popup blocked or failed, fall back to redirect
      if (errorCode === 'auth/popup-blocked' ||
          errorCode === 'auth/popup-closed-by-user' ||
          errorCode === 'auth/cancelled-popup-request') {
        console.log('Popup blocked or closed, using redirect...');
        await signInWithRedirect(auth, googleProvider);
        // This will redirect the page, so we won't reach here
        // Return a never-resolving promise
        return new Promise(() => {});
      }
      throw error;
    }
  },

  // Email/password authentication
  signInWithEmail: async (email: string, password: string): Promise<{ user: User; profile: UserProfile }> => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    const profile = await createOrUpdateUserProfile(result.user);
    return { user: result.user, profile };
  },

  signUpWithEmail: async (email: string, password: string): Promise<{ user: User; profile: UserProfile }> => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    const profile = await createOrUpdateUserProfile(result.user);
    return { user: result.user, profile };
  },

  // Password reset
  sendPasswordResetEmail: async (email: string): Promise<void> => {
    await sendPasswordResetEmail(auth, email);
  },

  // Change password (requires re-authentication)
  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error('No user signed in');

    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  },

  // Sign out
  signOut: async (): Promise<void> => {
    await firebaseSignOut(auth);
  },

  // Get current user
  getCurrentUser: (): User | null => {
    return auth.currentUser;
  },

  // Subscribe to auth state changes
  onAuthStateChanged: (callback: (user: User | null) => void) => {
    return onAuthStateChanged(auth, callback);
  },

  // Get user profile from Firestore (creates if doesn't exist)
  // Uses getDocFromServer to ensure fresh data, especially after onboarding
  getUserProfile: async (userId: string, user?: User): Promise<UserProfile | null> => {
    const userRef = doc(db, 'users', userId);
    try {
      // Try to get from server first for fresh data
      const userSnap = await getDocFromServer(userRef);
      if (userSnap.exists()) {
        return { id: userSnap.id, ...userSnap.data() } as UserProfile;
      }
    } catch {
      // If server fetch fails (offline), fall back to cache
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        return { id: userSnap.id, ...userSnap.data() } as UserProfile;
      }
    }
    // If profile doesn't exist and we have the user object, create it
    if (user) {
      return createOrUpdateUserProfile(user);
    }
    return null;
  },

  // Check if user is super admin using Firestore role (matches security rules)
  isSuperAdmin: (profile: UserProfile | null): boolean => {
    if (!profile) return false;
    return profile.role === 'super_admin';
  },

  // Check if user signed in with email/password (vs OAuth)
  isEmailUser: (): boolean => {
    const user = auth.currentUser;
    return user?.providerData.some(p => p.providerId === 'password') ?? false;
  },
};
